/**
 * ApplicationAnswers Page
 *
 * Lets candidates store the questions and answers they're already getting
 * asked on application forms ("Why do you want to work at X?", etc.) so they
 * can reuse them across jobs. The candidate authors the answer; the AI is
 * review-only and never rewrites — the whole point is to keep the candidate's
 * own voice and avoid AI-writing detectors.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Copy,
  Trash2,
  Sparkles,
  Loader2,
  X,
  ChevronDown,
  ChevronRight,
  Search,
} from 'lucide-react';
import { api } from '../services/api';
import type { ApplicationAnswer, UserJob } from '../types';

interface JobLite {
  jobid: string;
  label: string; // "Company / Title"
  active: boolean; // false → strikethrough in chips/picker
}

function jobIsActive(job: UserJob): boolean {
  // Anything not in Trash/Expired is fair game to show as active in the picker
  return job.jobphase !== 'Trash' && job.jobphase !== 'Expired';
}

export function ApplicationAnswers() {
  const [answers, setAnswers] = useState<ApplicationAnswer[]>([]);
  const [trial, setTrial] = useState(false);
  const [trialMax, setTrialMax] = useState(3);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [jobs, setJobs] = useState<JobLite[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Per-card expand state. Keyed by qaid; missing key → collapsed (default).
  // We auto-expand newly created/cloned rows so the user lands on an editable
  // card, not a closed shell with placeholder text.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleExpanded = (qaid: string) => {
    setExpanded((prev) => ({ ...prev, [qaid]: !prev[qaid] }));
  };

  // Load list + jobs on mount
  useEffect(() => {
    (async () => {
      try {
        const [resp, jobsResp] = await Promise.all([
          api.listApplicationAnswers(),
          api.listJobs(undefined, 'all'),
        ]);
        setAnswers(resp.answers || []);
        setTrial(!!resp.trial);
        setTrialMax(resp.trial_max ?? 3);
        setJobs(
          (jobsResp.jobs || []).map((j) => ({
            jobid: j.jobid,
            label: `${j.jobcompany || '(no company)'} / ${j.jobtitle || '(no title)'}`,
            active: jobIsActive(j),
          })),
        );
      } catch (err) {
        console.error('Failed to load application answers', err);
        setErrorMsg('Could not load your saved answers. Try again in a moment.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const atTrialCap = trial && answers.length >= trialMax;

  const upsertLocal = (updated: ApplicationAnswer) => {
    setAnswers((prev) => {
      const i = prev.findIndex((a) => a.qaid === updated.qaid);
      if (i === -1) return [updated, ...prev];
      const next = [...prev];
      next[i] = updated;
      return next;
    });
  };

  const handleAdd = async () => {
    if (atTrialCap) return;
    try {
      setBusyId('new');
      const created = await api.createApplicationAnswer('New question', '', []);
      setAnswers((prev) => [created, ...prev]);
      // Auto-expand so the user lands directly on the editable form
      setExpanded((prev) => ({ ...prev, [created.qaid]: true }));
    } catch (err) {
      console.error('Failed to add answer', err);
      alert('Could not add a new answer. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const handleSave = async (
    qaid: string,
    patch: Partial<Pick<ApplicationAnswer, 'question' | 'answer' | 'recommendations' | 'linked_jobids'>>,
  ) => {
    try {
      setBusyId(qaid);
      const updated = await api.updateApplicationAnswer(qaid, patch);
      upsertLocal(updated);
    } catch (err) {
      console.error('Failed to save answer', err);
      alert('Could not save your changes.');
    } finally {
      setBusyId(null);
    }
  };

  const handleClone = async (qaid: string) => {
    if (atTrialCap) return;
    try {
      setBusyId(qaid);
      const cloned = await api.cloneApplicationAnswer(qaid);
      setAnswers((prev) => [cloned, ...prev]);
      // Auto-expand the clone so the user can immediately tweak it
      setExpanded((prev) => ({ ...prev, [cloned.qaid]: true }));
    } catch (err) {
      console.error('Failed to clone answer', err);
      alert('Could not clone this answer.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (qaid: string) => {
    if (!confirm('Delete this Q&A? This cannot be undone.')) return;
    try {
      setBusyId(qaid);
      await api.deleteApplicationAnswer(qaid);
      setAnswers((prev) => prev.filter((a) => a.qaid !== qaid));
    } catch (err) {
      console.error('Failed to delete answer', err);
      alert('Could not delete this answer.');
    } finally {
      setBusyId(null);
    }
  };

  const handleFeedback = async (
    qaid: string,
    patch: Partial<Pick<ApplicationAnswer, 'question' | 'answer' | 'recommendations' | 'linked_jobids'>>,
  ) => {
    try {
      setFeedbackId(qaid);
      // Save any unsaved changes before requesting feedback
      await api.updateApplicationAnswer(qaid, patch);
      const updated = await api.getApplicationAnswerFeedback(qaid);
      upsertLocal(updated);
    } catch (err: any) {
      console.error('Failed to get feedback', err);
      const msg = err?.response?.data?.message || 'Could not generate feedback right now.';
      alert(msg);
    } finally {
      setFeedbackId(null);
    }
  };

  // Keyword filter on question + answer (case-insensitive substring).
  const filteredAnswers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return answers;
    return answers.filter((a) => {
      const haystack = `${a.question}\n${a.answer}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [answers, searchQuery]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header — minimal, with a clear path back to the main app */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <a href="/" className="flex items-center space-x-2">
              <img src="/assets/skillsnap_logo.png" alt="SkillSnap" className="h-8 w-auto" />
              <span className="text-xl font-semibold text-primary-600">SkillSnap</span>
            </a>
            <Link
              to="/Ready"
              data-help-target="answers.backtojobs"
              className="flex items-center space-x-1.5 px-3 py-1.5 text-sm text-gray-700 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Jobs</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page intro */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Application Answers</h1>
          <p className="text-sm text-gray-700 leading-relaxed">
            Some application forms ask personal questions like{' '}
            <em>&ldquo;Why do you want to work at this company?&rdquo;</em>. Save your
            answers here so you can reuse them, or use them as a starting point when a
            similar question comes up at another company.
          </p>
          <p className="text-sm text-gray-700 leading-relaxed mt-2">
            <strong>These answers are not AI-generated.</strong> The whole point is that
            the answer is in <em>your</em> voice — that's what avoids AI-writing
            detectors and gives you a genuine, distinctive response. The{' '}
            <strong>Get Feedback</strong> button asks an AI to{' '}
            <em>review</em> your answer (note unclear phrasing, vague claims, tone) but
            it is forbidden from rewriting it for you.
          </p>
          {trial && (
            <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Trial accounts can save up to <strong>{trialMax}</strong> answers and
              cannot use AI feedback. Upgrade to Active for unlimited answers and the
              feedback button.
            </div>
          )}
        </div>

        {/* Add button + Search */}
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <button
            onClick={handleAdd}
            data-help-target="answers.addqa"
            disabled={atTrialCap || busyId === 'new'}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              atTrialCap
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
            title={atTrialCap ? `Trial limit of ${trialMax} reached` : 'Add a new Q&A'}
          >
            <Plus className="w-4 h-4" />
            <span>{busyId === 'new' ? 'Adding…' : 'Add Q&A'}</span>
          </button>
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-help-target="answers.search"
              placeholder="Search questions and answers…"
              className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Search answers"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <span className="text-xs text-gray-500 sm:whitespace-nowrap">
            {searchQuery
              ? `${filteredAnswers.length} of ${answers.length}`
              : `${answers.length} saved${trial ? ` of ${trialMax}` : ''}`}
          </span>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : errorMsg ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
            {errorMsg}
          </div>
        ) : answers.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
            <p className="text-sm">
              No saved answers yet. Click <strong>Add Q&A</strong> to start.
            </p>
          </div>
        ) : filteredAnswers.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
            <p className="text-sm">
              No answers match <strong>&ldquo;{searchQuery}&rdquo;</strong>.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredAnswers.map((qa, i) => (
              <AnswerCard
                key={qa.qaid}
                helpTarget={i === 0 ? 'answers.answer0' : undefined}
                answer={qa}
                jobs={jobs}
                isTrial={trial}
                isBusy={busyId === qa.qaid}
                isFeedbackBusy={feedbackId === qa.qaid}
                atTrialCap={atTrialCap}
                isExpanded={!!expanded[qa.qaid]}
                onToggleExpanded={() => toggleExpanded(qa.qaid)}
                onSave={(patch) => handleSave(qa.qaid, patch)}
                onClone={() => handleClone(qa.qaid)}
                onDelete={() => handleDelete(qa.qaid)}
                onFeedback={(patch) => handleFeedback(qa.qaid, patch)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Footer links match the rest of the app */}
      <footer className="border-t border-gray-200 bg-white py-4">
        <div className="flex justify-center gap-4 text-xs text-gray-500">
          <a
            href="/docs/toc.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary-600 transition-colors"
          >
            Terms
          </a>
          <a
            href="/docs/pp.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary-600 transition-colors"
          >
            Privacy
          </a>
        </div>
      </footer>
    </div>
  );
}

interface AnswerCardProps {
  answer: ApplicationAnswer;
  helpTarget?: string;
  jobs: JobLite[];
  isTrial: boolean;
  isBusy: boolean;
  isFeedbackBusy: boolean;
  atTrialCap: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onSave: (
    patch: Partial<Pick<ApplicationAnswer, 'question' | 'answer' | 'recommendations' | 'linked_jobids'>>,
  ) => void;
  onClone: () => void;
  onDelete: () => void;
  onFeedback: (
    patch: Partial<Pick<ApplicationAnswer, 'question' | 'answer' | 'recommendations' | 'linked_jobids'>>,
  ) => void;
}

function AnswerCard({
  answer,
  helpTarget,
  jobs,
  isTrial,
  isBusy,
  isFeedbackBusy,
  atTrialCap,
  isExpanded,
  onToggleExpanded,
  onSave,
  onClone,
  onDelete,
  onFeedback,
}: AnswerCardProps) {
  const [question, setQuestion] = useState(answer.question);
  const [answerText, setAnswerText] = useState(answer.answer);
  const [recs, setRecs] = useState(answer.recommendations || '');
  const [linked, setLinked] = useState<string[]>(answer.linked_jobids || []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  // Re-sync if the parent updates this row (e.g. after Get Feedback)
  useEffect(() => {
    setQuestion(answer.question);
    setAnswerText(answer.answer);
    setRecs(answer.recommendations || '');
    setLinked(answer.linked_jobids || []);
  }, [answer]);

  const dirty =
    question !== answer.question ||
    answerText !== answer.answer ||
    recs !== (answer.recommendations || '') ||
    JSON.stringify(linked) !== JSON.stringify(answer.linked_jobids || []);

  const jobsById = useMemo(() => {
    const m = new Map<string, JobLite>();
    jobs.forEach((j) => m.set(j.jobid, j));
    return m;
  }, [jobs]);

  const linkedChips = linked.map((jid) => {
    const j = jobsById.get(jid);
    if (!j) {
      // Job no longer exists (expired / deleted). Show greyed strikethrough so
      // the user can decide whether to remove the dead link.
      return { jid, label: '(deleted job)', active: false };
    }
    return { jid, label: j.label, active: j.active };
  });

  const toggleJobLink = (jid: string) => {
    setLinked((prev) => (prev.includes(jid) ? prev.filter((x) => x !== jid) : [...prev, jid]));
  };

  // Collapsed-state summary metadata
  const linkedCount = linked.length;
  const hasAnswer = (answer.answer || '').trim().length > 0;
  const hasRecs = (answer.recommendations || '').trim().length > 0;
  const headerQuestion = (answer.question || '').trim() || '(untitled question)';

  return (
    <div data-help-target={helpTarget} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      {/* Collapsible header — click anywhere to toggle */}
      <button
        type="button"
        onClick={onToggleExpanded}
        aria-expanded={isExpanded}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />
        )}
        <span
          className={`flex-1 min-w-0 truncate text-sm font-medium ${
            hasAnswer || answer.question ? 'text-gray-900' : 'text-gray-400 italic'
          }`}
          title={headerQuestion}
        >
          {headerQuestion}
        </span>
        <span className="hidden sm:flex items-center gap-1 text-xs text-gray-400 shrink-0">
          {!hasAnswer && (
            <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded">
              empty
            </span>
          )}
          {hasRecs && (
            <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded">
              feedback
            </span>
          )}
          {linkedCount > 0 && (
            <span className="px-1.5 py-0.5 bg-primary-50 text-primary-700 border border-primary-200 rounded">
              {linkedCount} linked
            </span>
          )}
        </span>
      </button>

      {/* Body — only rendered when expanded */}
      {isExpanded && (
      <div className="px-4 pb-4 pt-1">
      {/* Question */}
      <div className="mb-3">
        <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
          Question
        </label>
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g. Why do you want to work at this company?"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {/* Answer */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs uppercase tracking-wide text-gray-500">
            Your Answer
          </label>
          {answerText.trim() && (
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(answerText); }}
              className="flex items-center gap-1 px-1.5 py-0.5 text-xs text-gray-400 hover:text-primary-600 rounded hover:bg-gray-100 transition-colors"
              title="Copy answer to clipboard"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          )}
        </div>
        <textarea
          value={answerText}
          onChange={(e) => setAnswerText(e.target.value)}
          rows={6}
          placeholder="Write your answer in your own words…"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {/* Recommendations (read/edit) */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs uppercase tracking-wide text-gray-500">
            AI Recommendations
          </label>
          {recs && (
            <button
              onClick={() => setRecs('')}
              className="text-xs text-gray-500 hover:text-red-600 transition-colors"
              title="Clear recommendations"
            >
              clear
            </button>
          )}
        </div>
        <textarea
          value={recs}
          onChange={(e) => setRecs(e.target.value)}
          rows={recs ? Math.min(10, Math.max(3, recs.split('\n').length)) : 3}
          placeholder="Click 'Get Feedback' to ask the AI for a light review of your answer."
          className="w-full px-3 py-2 border border-gray-200 bg-amber-50/40 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {/* Linked jobs */}
      <div className="mb-3">
        <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
          Linked Jobs (optional)
        </label>
        <div className="flex flex-wrap gap-2 mb-2">
          {linkedChips.length === 0 && (
            <span className="text-xs text-gray-400">No jobs linked</span>
          )}
          {linkedChips.map((chip) => (
            <span
              key={chip.jid}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${
                chip.active
                  ? 'bg-primary-50 border-primary-200 text-primary-700'
                  : 'bg-gray-100 border-gray-300 text-gray-500 line-through'
              }`}
              title={chip.active ? '' : 'This job no longer exists or has expired.'}
            >
              {chip.label}
              <button
                onClick={() => toggleJobLink(chip.jid)}
                className="hover:text-red-600"
                aria-label="Remove link"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="relative inline-block" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) { setPickerOpen(false); setPickerSearch(''); } }}>
          <button
            onClick={() => { setPickerOpen((v) => !v); setPickerSearch(''); }}
            className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
          >
            Add link <ChevronDown className="w-3 h-3" />
          </button>
          {pickerOpen && (
            <div className="absolute left-0 bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-80 max-h-72 flex flex-col">
              <div className="p-1.5 border-b border-gray-100">
                <input
                  type="text"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Search jobs..."
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-400"
                  autoFocus
                />
              </div>
              <div className="overflow-y-auto flex-1">
                {jobs.length === 0 && (
                  <div className="px-3 py-2 text-xs text-gray-400">No jobs yet.</div>
                )}
                {[...jobs]
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .filter((j) => !pickerSearch || j.label.toLowerCase().includes(pickerSearch.toLowerCase()))
                  .map((j) => {
                    const checked = linked.includes(j.jobid);
                    return (
                      <label
                        key={j.jobid}
                        className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-xs"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleJobLink(j.jobid)}
                        />
                        <span className={j.active ? '' : 'line-through text-gray-500'}>
                          {j.label}
                        </span>
                      </label>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action row */}
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
        <button
          onClick={() => onSave({ question, answer: answerText, recommendations: recs, linked_jobids: linked })}
          disabled={!dirty || isBusy}
          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
            dirty && !isBusy
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
          title={dirty ? 'Save changes' : 'No unsaved changes'}
        >
          {isBusy ? 'Saving…' : 'Save'}
        </button>

        <button
          onClick={() => onFeedback({ question, answer: answerText, recommendations: recs, linked_jobids: linked })}
          disabled={isTrial || isFeedbackBusy || !answerText.trim()}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
            isTrial || !answerText.trim()
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-amber-500 text-white hover:bg-amber-600'
          }`}
          title={
            isTrial
              ? 'Upgrade to Active to use AI feedback'
              : !answerText.trim()
                ? 'Write an answer first'
                : 'Light AI review of your answer (no rewriting)'
          }
        >
          {isFeedbackBusy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          <span>{isFeedbackBusy ? 'Reviewing…' : 'Get Feedback'}</span>
        </button>

        <button
          onClick={onClone}
          disabled={atTrialCap || isBusy}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
            atTrialCap
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
          title={atTrialCap ? `Trial limit reached` : 'Make a copy of this Q&A'}
        >
          <Copy className="w-4 h-4" />
          <span>Clone</span>
        </button>

        <div className="flex-1" />

        <button
          onClick={onDelete}
          disabled={isBusy}
          className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
          title="Delete this Q&A"
        >
          <Trash2 className="w-4 h-4" />
          <span>Delete</span>
        </button>
      </div>
      </div>
      )}
    </div>
  );
}
